import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import i18n from 'i18next';
import {
  PageMetadata,
  AdminProductVariant,
  LocalPropertyValue,
  Price,
  MultiVariantProductOverview,
  SingleVariantProductOverview,
} from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '.';
import { productServiceAdminProductsClient, productServiceAdminProductVariantsClient } from '../api-client/product-service-client';
import { ProductsListItem } from '../features/Products/ProductList/ProductsList';
import { getSKUsCountByListItems } from '../utils/products';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { setNotification } from './ui';
import { resolveAxiosErrorResponse } from '../utils/errors';

type ProductId = number;
export interface PropertyDetail extends LocalPropertyValue {
  property_name?: string;
  type?: string;
}
export interface ProductVariant extends AdminProductVariant {
  properties?: PropertyDetail[];
}

export type AdminProductType = MultiVariantProductOverview & SingleVariantProductOverview;
export type AdminProductTypeString = 'MultiVariantProductOverview' | 'SingleVariantProductOverview';

interface AdminProductsState extends EntriesState<AdminProductType> {
  pageMetadata: PageMetadata;
  variantsByProduct: Record<ProductId, ProductVariant[]>;
}

export const initialAdminProductsState: AdminProductsState = {
  ...initialEntriesState,
  pageMetadata: {
    current_page: 0,
    size: 0,
    total_items: 0,
    total_pages: 0,
  },
  variantsByProduct: {},
};

const adminProductsAdapter = createEntityAdapter<AdminProductType>();

// thunks
export const fetchAdminProducts = createAsyncThunk(
  'adminProducts/fetchAdminProductsStatus',
  async (
    {
      goToPageNumber,
      numberOfItems,
      searchText,
      brand,
      categoryIds,
    }: {
      goToPageNumber: number | undefined;
      numberOfItems: number | undefined;
      searchText: string | undefined;
      brand: string | undefined;
      categoryIds: number[] | undefined;
    },
    { rejectWithValue, dispatch },
  ) => {
    dispatch(clearVariantsByProduct());
    try {
      const { data } = await productServiceAdminProductsClient.getAdminProducts(
        goToPageNumber?.toString(),
        numberOfItems?.toString(),
        brand,
        searchText,
        categoryIds,
      );

      return data;
    } catch (error) {
      return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
    }
  },
);

export const fetchProductVariants = createAsyncThunk('adminProducts/fetchProductVariantsStatus', async (productId: number) => {
  const response = await productServiceAdminProductsClient.getAdminProduct(productId);
  return response.data;
});

export const deleteProductAndVariants = createAsyncThunk(
  'adminProducts/deleteProductAndVariantsStatus',
  async (productListItems: ProductsListItem[], { rejectWithValue, dispatch }) => {
    try {
      for (const product of productListItems) {
        const { id, parent_id } = product;
        if (!parent_id) {
          // Product
          await productServiceAdminProductsClient.deleteAdminProduct(id);
        } else {
          // Variant
          await productServiceAdminProductVariantsClient.deleteProductVariant(parent_id, id);
        }
      }

      const deletedSKUsCount = getSKUsCountByListItems(productListItems);
      const notificationText = i18n.t('productsscreen:notification.skuDeleted', { count: deletedSKUsCount });
      dispatch(setNotification({ text: notificationText, show: true, type: 'success' }));
    } catch (error) {
      const notificationText = i18n.t('productsscreen:notification.errorDeletingProducts');
      const axiosErrorResponseData = resolveAxiosErrorResponse(error)?.data;
      const errorMessage = axiosErrorResponseData?.message || '';
      const errorText = `${notificationText}: ${errorMessage}`;
      dispatch(
        setNotification({
          text: errorText,
          show: true,
          type: 'warning',
        }),
      );
      return rejectWithValue(axiosErrorResponseData);
    }
  },
);

// slice
const adminProducts = createSlice({
  name: 'adminProducts',
  initialState: adminProductsAdapter.getInitialState(initialAdminProductsState),
  reducers: {
    clearVariantsByProduct(state) {
      state.variantsByProduct = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminProducts.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminProducts.fulfilled, (state, action) => {
      if (action.payload) {
        adminProductsAdapter.setAll(state, action.payload.products as AdminProductType[]);
        state.pageMetadata = action.payload.page_metadata;
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminProducts.rejected, state => {
      state.loading = Loading.FAILED;
    });
    builder.addCase(fetchProductVariants.fulfilled, (state, action) => {
      if (action.payload) {
        const { product, properties, property_values } = action.payload;
        const { id, variants } = product;

        if (variants && property_values && properties && id) {
          // Make a Record collection of properties
          const propertyValues: Record<number, LocalPropertyValue> = {};
          property_values.forEach(propertyValue => {
            if (propertyValue.id) {
              propertyValues[propertyValue.id] = propertyValue;
            }
          });

          // Combine variants and property information together
          const expandedVariants: ProductVariant[] = variants.map(variant => {
            let variantProperty: PropertyDetail[] = [];
            if (variant.property_value_ids) {
              variantProperty = variant.property_value_ids.map(valueId => {
                return propertyValues[valueId];
              });
            }

            return {
              ...variant,
              properties: variantProperty,
            };
          });
          state.variantsByProduct[id] = expandedVariants.length === 0 ? variants : expandedVariants;
        }
      }
    });
    builder.addCase(deleteProductAndVariants.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(deleteProductAndVariants.fulfilled, state => {
      state.loading = Loading.SUCCEEDED;
    });
    builder.addCase(deleteProductAndVariants.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { clearVariantsByProduct } = adminProducts.actions;

export default adminProducts.reducer;

// selectors

export const { selectAll: selectAllProducts, selectById: selectProductById } = adminProductsAdapter.getSelectors(
  (state: RootState) => state.adminProducts,
);

export const selectAdminProductsLoading = (state: RootState) => state.adminProducts.loading;
export const selectPageMetadata = (state: RootState) => state.adminProducts.pageMetadata;

const selectVariants = (state: RootState) => state.adminProducts.variantsByProduct;
const selectProductEntities = (state: RootState) => state.adminProducts.entities;

export const selectListItems = createSelector(
  [selectVariants, selectAllProducts, selectProductEntities],
  (variants, products, productEntities): ProductsListItem[] => {
    if (!variants) {
      return products;
    }

    let productsAndVariants: ProductsListItem[] = [];

    for (const productId in variants) {
      const variantsWithProperties = variants[productId].map(variant => {
        let sales_channel_uuids: string[] = [];
        let prices: Price[] = [];

        if (variant && variant.prices) {
          sales_channel_uuids = variant.prices.map(price => price.sales_channel_uuid) as string[];
          prices = variant.prices;
        }

        // Expand variant content
        return {
          ...(productEntities[productId] || {}),
          id: variant.id,
          sku: variant.sku,
          parent_id: Number(productId),
          properties: variant.properties,
          sales_channel_uuids,
          prices: prices,
        };
      });
      // Dismiss single product variants
      if (variantsWithProperties.length > 1) {
        productsAndVariants = [...productsAndVariants, ...variantsWithProperties];
      }
    }

    const productWithMultipleVariants = products.filter(product => product.variants_count && product.variants_count > 1);
    const singleProducts = products
      .filter(product => (product.type as AdminProductTypeString) === 'SingleVariantProductOverview')
      .map(product => {
        const singleVariant = variants[product.id] && variants[product.id][0];
        if (!singleVariant) {
          return product;
        }
        const { id: variantId, prices = [], properties = [] } = singleVariant;
        const salesChannelUuids = prices.map(price => price.sales_channel_uuid) as string[];
        return {
          ...product,
          prices: [...prices],
          properties: properties,
          sales_channel_uuids: salesChannelUuids,
          variant_id: variantId,
        };
      });
    return [...productWithMultipleVariants, ...singleProducts, ...productsAndVariants];
  },
);
